import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { LOGO_DARK } from "@/lib/constants";
import TrackMap from "@/components/TrackMap";
import {
  ArrowRight, BellRing, CalendarClock, CheckCircle2, CloudRainWind, ClipboardList, FileDown, Loader2, MailCheck,
  MapPin, Navigation, PackageCheck, RefreshCw, Search, Truck,
} from "lucide-react";

const pub = axios.create({ baseURL: API });

const STEPS = [
  { label: "Booked", icon: PackageCheck },
  { label: "In Transit", icon: Truck },
  { label: "Out for Delivery", icon: Navigation },
  { label: "Delivered", icon: CheckCircle2 },
];

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return isNaN(d) ? String(v).slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0B0F14] text-zinc-100" data-testid="track-page">
      <div className="border-b border-white/[0.07] bg-[#0D1219]">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4">
          <img src={LOGO_DARK} alt="Premier Handling Solutions" className="h-9 w-auto brightness-0 invert" />
          <div className="ml-auto text-right">
            <div className="font-heading text-sm font-bold tracking-wide">SHIPMENT TRACKING</div>
            <div className="text-[11px] text-zinc-500">Premier Handling Solutions · PHS Lift</div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-5 py-8">{children}</div>
      <div className="mx-auto max-w-3xl px-5 pb-10 text-center text-xs text-zinc-600">
        Questions about your shipment? Reply to your PHS confirmation email or contact your PHS representative.
      </div>
    </div>
  );
}

function LookupForm() {
  const navigate = useNavigate();
  const [bol, setBol] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await pub.post("/public/track/lookup", { bol_number: bol, zip });
      navigate(`/track/${data.token}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(211,100%,50%)]/15">
          <Truck className="h-7 w-7 text-[hsl(211,100%,50%)]" />
        </div>
        <h1 className="font-heading text-2xl font-bold">Track your freight</h1>
        <p className="mt-1.5 text-sm text-zinc-400">
          Enter the BOL or PRO number from your booking confirmation and the delivery ZIP code.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-white/[0.08] bg-[#101722] p-5">
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-zinc-500">BOL / PRO number</label>
          <input value={bol} onChange={(e) => setBol(e.target.value)} placeholder="e.g. 32902489 or WB9013068"
            data-testid="track-lookup-bol"
            className="w-full rounded-md border border-white/10 bg-[#0B0F14] px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-zinc-600 focus:border-[hsl(211,100%,50%)]" />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-zinc-500">Delivery ZIP code</label>
          <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 60123" inputMode="numeric"
            data-testid="track-lookup-zip"
            className="w-full rounded-md border border-white/10 bg-[#0B0F14] px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-zinc-600 focus:border-[hsl(211,100%,50%)]" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="track-lookup-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="track-lookup-submit"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[hsl(211,100%,50%)] py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track shipment
        </button>
      </form>
    </div>
  );
}

function Progress({ stage }) {
  return (
    <div className="mt-6" data-testid="track-progress">
      <div className="relative mx-2 h-1 rounded-full bg-white/10">
        <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500 transition-all duration-700"
          style={{ width: `${(stage / (STEPS.length - 1)) * 100}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-1">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i <= stage;
          const current = i === stage;
          return (
            <div key={s.label} className="flex flex-col items-center gap-1.5 text-center">
              <div className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                active ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-400" : "border-white/10 bg-white/[0.03] text-zinc-600"
              } ${current ? "ring-2 ring-emerald-500/30" : ""}`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className={`text-[11px] font-semibold ${active ? "text-zinc-200" : "text-zinc-600"}`}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Fact({ label, value, testid }) {
  if (!value) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-zinc-100">{value}</div>
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) {
    return <p className="py-6 text-center text-sm text-zinc-500">No carrier scans yet — check back after pickup.</p>;
  }
  return (
    <div className="space-y-0" data-testid="track-timeline">
      {events.map((ev, i) => (
        <div key={i} className="relative flex gap-3 pb-4 last:pb-0">
          <div className="flex flex-col items-center">
            <div className={`mt-1 h-2.5 w-2.5 rounded-full ${i === 0 ? "bg-emerald-400" : "bg-zinc-600"}`} />
            {i < events.length - 1 && <div className="w-px flex-1 bg-white/10" />}
          </div>
          <div className="min-w-0 pb-1">
            <div className={`text-sm font-semibold ${i === 0 ? "text-zinc-100" : "text-zinc-400"}`}>{ev.status || "Update"}</div>
            <div className="text-xs text-zinc-500">
              {[[ev.city, ev.state].filter(Boolean).join(", "), ev.time].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function WeatherAdvisory({ weather }) {
  const risks = [
    { side: "Pickup area", r: weather?.origin },
    { side: "Delivery area", r: weather?.destination },
  ].filter((x) => x.r && x.r.score > 0);
  if (!risks.length) return null;
  const high = risks.some((x) => x.r.score === 2);
  return (
    <div className={`rounded-xl border p-4 ${high ? "border-red-500/40 bg-red-500/[0.07]" : "border-amber-500/40 bg-amber-500/[0.07]"}`}
      data-testid="track-weather-advisory">
      <div className={`flex items-center gap-2 text-sm font-bold ${high ? "text-red-400" : "text-amber-400"}`}>
        <CloudRainWind className="h-4 w-4" /> Weather advisory on this route
      </div>
      <div className="mt-2 space-y-1.5">
        {risks.map(({ side, r }) => (
          <div key={side} className="text-sm text-zinc-300">
            <span className="font-semibold">{side}</span>
            {r.place ? ` (${r.place})` : ""}: {r.label}
            {r.date ? ` expected ${fmtDate(r.date)}` : ""}
            {r.wind_mph >= 28 ? ` · winds to ${Math.round(r.wind_mph)} mph` : ""}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-zinc-500">Pickup or delivery may be delayed. We're monitoring this load closely.</p>
    </div>
  );
}

function NotifyOptIn({ token, delivered }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  if (delivered) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() && !phone.trim()) {
      setError("Enter an email address or mobile number.");
      return;
    }
    setBusy(true);
    try {
      await pub.post(`/public/track/${token}/subscribe`, { email, phone });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not subscribe — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#101722] p-5" data-testid="track-notify-optin">
      {done ? (
        <div className="flex items-center gap-2.5 text-sm font-semibold text-emerald-400" data-testid="track-notify-success">
          <MailCheck className="h-5 w-5" /> You're subscribed — we'll send status changes and weather delay alerts.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-200">
            <BellRing className="h-4 w-4 text-[hsl(211,100%,50%)]" /> Get email or text updates for this shipment
          </div>
          <p className="mt-1 text-xs text-zinc-500">Status changes and delivery confirmation for this shipment.</p>
          <form onSubmit={submit} className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" data-testid="track-notify-email"
              className="min-w-0 flex-1 rounded-md border border-white/10 bg-[#0B0F14] px-3 py-2 text-sm outline-none transition-colors placeholder:text-zinc-600 focus:border-[hsl(211,100%,50%)]" />
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile # for texts (optional)" data-testid="track-notify-phone"
              className="min-w-0 flex-1 rounded-md border border-white/10 bg-[#0B0F14] px-3 py-2 text-sm outline-none transition-colors placeholder:text-zinc-600 focus:border-[hsl(211,100%,50%)]" />
            <button type="submit" disabled={busy} data-testid="track-notify-submit"
              className="flex items-center justify-center gap-1.5 rounded-md bg-[hsl(211,100%,50%)] px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Notify me"}
            </button>
          </form>
          {error && <p className="mt-2 text-xs text-red-400" data-testid="track-notify-error">{error}</p>}
        </>
      )}
    </div>
  );
}

function ShipmentView({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (manual = false) => {
    if (manual) setBusy(true);
    try {
      const { data: d } = await pub.get(`/public/track/${token}`);
      setData(d);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load tracking.");
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-white/[0.08] bg-[#101722] p-8 text-center" data-testid="track-error">
        <p className="text-sm text-red-400">{error}</p>
        <a href="/track" className="mt-4 inline-block text-sm font-semibold text-[hsl(211,100%,50%)] hover:underline">
          Track by BOL / PRO number instead
        </a>
      </div>
    );
  }
  if (!data) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-zinc-600" /></div>;
  }

  const place = (p) => [p?.city, p?.state].filter(Boolean).join(", ") || p?.zip || "—";
  const delivered = data.delivered;

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border p-6 ${delivered ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-white/[0.08] bg-[#101722]"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
              Shipment {data.quote_number}{data.customer ? ` · ${data.customer}` : ""}
            </div>
            <h1 className={`mt-1 font-heading text-2xl font-bold sm:text-3xl ${delivered ? "text-emerald-400" : "text-zinc-100"}`} data-testid="track-status">
              {delivered && <CheckCircle2 className="mr-2 inline h-7 w-7 -translate-y-0.5" />}{data.status}
            </h1>
            {data.current_location && (
              <p className="mt-1 flex items-center gap-1 text-sm text-zinc-400">
                <MapPin className="h-3.5 w-3.5" /> Currently near {data.current_location}
              </p>
            )}
          </div>
          <button onClick={() => load(true)} disabled={busy} data-testid="track-refresh"
            className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-semibold text-zinc-300 transition-colors hover:border-white/25">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <Progress stage={data.stage} />
        {delivered && data.has_pod && (
          <a href={`${API}/public/track/${token}/pod`} data-testid="track-pod-download"
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-4 py-2 text-sm font-bold text-emerald-400 transition-colors hover:bg-emerald-500/20">
            <FileDown className="h-4 w-4" /> Download signed POD
          </a>
        )}
      </div>

      {!delivered && <WeatherAdvisory weather={data.weather} />}

      <TrackMap data={data} />

      <div className="rounded-xl border border-white/[0.08] bg-[#101722] p-5">
        <div className="flex flex-wrap items-center gap-2 text-base font-semibold">
          <span>{place(data.origin)}</span>
          <ArrowRight className="h-4 w-4 text-[hsl(211,100%,50%)]" />
          <span>{place(data.destination)}</span>
          {data.destination?.zip && <span className="text-sm font-normal text-zinc-500">{data.destination.zip}</span>}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Fact label="Carrier" value={data.carrier} testid="track-carrier" />
          <Fact label={data.bol_type === "GlobalTranz" ? "BOL number" : "PRO number"} value={data.bol_number} testid="track-bol" />
          <Fact label="Pickup date" value={fmtDate(data.pickup_date)} testid="track-pickup" />
          {delivered
            ? <Fact label="Delivered" value={fmtDate(data.delivery_date) || "Confirmed"} testid="track-delivered" />
            : <Fact label="Estimated delivery" value={fmtDate(data.estimated_delivery)} testid="track-eta" />}
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-[#101722] p-5">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
          <ClipboardList className="h-3.5 w-3.5" /> Tracking history
        </div>
        <Timeline events={data.events} />
        {data.checked_at && (
          <p className="mt-3 flex items-center gap-1 border-t border-white/[0.06] pt-3 text-[11px] text-zinc-600">
            <CalendarClock className="h-3 w-3" /> Last updated {new Date(data.checked_at).toLocaleString()}
          </p>
        )}
      </div>

      <NotifyOptIn token={token} delivered={delivered} />
    </div>
  );
}

export default function PublicTracking() {
  const { token } = useParams();
  return <Shell>{token ? <ShipmentView token={token} /> : <LookupForm />}</Shell>;
}
